import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, HTTPError, patch, post } from "common/helpers/HTTP";
import type { Campaign } from "common/types";

import useUpdateCampaign from "./useUpdateCampaign";

import type { DetailedCampaign } from "../types";

type NewCampaign = Pick<
  DetailedCampaign,
  | "name"
  | "type"
  | "outreach_type"
  | "miner"
  | "exclude_active_targets"
  | "exclude_previous_targets"
  | "no_pfp"
> & {
  account_ids: string[];
  audience_size?: number; // For "DRIP" campaign, don't send "audience_size"
  inmail_optimization?: boolean; // For "DRIP" campaign send "inmail_optimization" false
};

type CampaignUpdates = Partial<
  Pick<
    DetailedCampaign,
    | "name"
    | "inmail_optimization"
    | "state"
    | "labels"
    | "miner"
    | "crm_type"
    | "exclude_active_targets"
    | "exclude_previous_targets"
    | "no_pfp"
  >
>;

async function createCampaignRequest(
  workspaceId: string,
  newCampaign: NewCampaign,
): Promise<Campaign> {
  return (await post(`workspaces/${workspaceId}/campaigns`, newCampaign))
    .campaign;
}
async function updateCampaignRequest(
  workspaceId: string,
  campaignId: string,
  updates: CampaignUpdates,
) {
  const { labels, ...rest } = updates;

  let requestPayload: Omit<CampaignUpdates, "labels"> & {
    autolabel_ids?: string[];
  };

  if (labels) {
    requestPayload = { ...rest, autolabel_ids: labels.map(({ id }) => id) };
  } else {
    requestPayload = updates;
  }

  await patch(
    `workspaces/${workspaceId}/campaigns/${campaignId}`,
    requestPayload,
  );
}
async function deleteCampaignRequest(
  workspaceId: string,
  campaignId: string,
  abortSignal?: AbortSignal,
) {
  await del(`workspaces/${workspaceId}/campaigns/${campaignId}`, abortSignal);
}

async function scheduleCampaignRequest(
  workspaceId: string,
  campaignId: string,
  startDate: string,
) {
  await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/schedule-start`,
    { start_date: startDate },
  );
}
async function unscheduleCampaignRequest(
  workspaceId: string,
  campaignId: string,
) {
  await del(`workspaces/${workspaceId}/campaigns/${campaignId}/schedule-start`);
}

async function duplicateCampaignRequest(
  workspaceId: string,
  campaignId: string,
  updates: {
    accountIds: string[];
    name?: string;
    miner?: string;
  },
): Promise<Campaign> {
  const { accountIds, ...rest } = updates;

  return (
    await post(`workspaces/${workspaceId}/campaigns/${campaignId}/duplicate`, {
      account_ids: accountIds,
      ...rest,
    })
  ).campaign;
}

async function removeAccountFromCampaignRequest(
  workspaceId: string,
  campaignId: string,
  accountId: string,
) {
  await del(
    `workspaces/${workspaceId}/campaigns/${campaignId}/accounts/${accountId}`,
  );
}

async function addAccountToCampaignRequest(
  workspaceId: string,
  campaignId: string,
  accountId: string,
) {
  await post(`workspaces/${workspaceId}/campaigns/${campaignId}/accounts`, {
    account_id: accountId,
  });
}

export default function useCampaignMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { campaignsKeys } = getQueryKeys(workspaceId);

  const {
    updateCachedCampaign,
    updateCachedCampaigns,
    updateCachedCampaignInList,
  } = useUpdateCampaign();

  const { mutateAsync: createCampaign, isPending: isCreatingCampaign } =
    useMutation({
      mutationFn: (newCampaign: NewCampaign) =>
        createCampaignRequest(workspaceId, newCampaign),
      onError: () => toast.error("Error creating campaign"),
      onSuccess: (campaign) => {
        updateCachedCampaigns((campaigns) => [campaign, ...campaigns]);
      },
    });

  const { mutateAsync: updateCampaign, isPending: isUpdatingCampaign } =
    useMutation({
      mutationFn: ({
        campaignId,
        updates,
      }: {
        campaignId: string;
        updates: CampaignUpdates;
      }) => updateCampaignRequest(workspaceId, campaignId, updates),
      onMutate: async ({ campaignId, updates }) => {
        await queryClient.cancelQueries({
          queryKey: campaignsKeys.details(campaignId),
        });

        const previousCampaign = queryClient.getQueryData(
          campaignsKeys.details(campaignId),
        );

        if (!updates.state) {
          updateCachedCampaign(campaignId, (draftCampaign) => ({
            ...draftCampaign,
            ...updates,
          }));

          const {
            inmail_optimization: _isInMail,
            labels: _labels,
            ...rest
          } = updates;

          updateCachedCampaignInList(campaignId, rest);
        }

        return { previousCampaign };
      },
      onError: (_, { campaignId }, { previousCampaign }) => {
        queryClient.setQueryData(
          campaignsKeys.details(campaignId),
          previousCampaign,
        );

        toast.error(`Error updating campaign`);
      },
      onSuccess: (_, { campaignId, updates }) => {
        if (updates.state) {
          updateCachedCampaign(campaignId, (draftCampaign) => {
            draftCampaign.state = updates.state;
          });

          updateCachedCampaignInList(campaignId, {
            state: updates.state,
          });
        }
      },
    });

  const { mutateAsync: deleteCampaign, isPending: isDeletingCampaign } =
    useMutation({
      mutationFn: ({
        campaignId,
        abortSignal,
      }: {
        campaignId: string;
        abortSignal?: AbortSignal;
      }) => deleteCampaignRequest(workspaceId, campaignId, abortSignal),
      onSuccess: (_, { campaignId, abortSignal }) => {
        updateCachedCampaigns((campaigns) =>
          campaigns.filter(({ id }) => id !== campaignId),
        );

        // Don't display success toast notification for batch updates
        if (!abortSignal) {
          toast.success("Campaign deleted");
        }
      },
      onError: (error, { abortSignal }) => {
        // Don't display error toast notification for batch updates
        if (!abortSignal && error instanceof HTTPError && error.serverMessage) {
          toast.error(error.serverMessage);
        }
      },
    });

  const { mutateAsync: scheduleCampaign, isPending: isSchedulingCampaign } =
    useMutation({
      mutationFn: ({
        campaignId,
        startDate,
      }: {
        campaignId: string;
        startDate: string;
      }) => scheduleCampaignRequest(workspaceId, campaignId, startDate),
      onSuccess: (_, { campaignId, startDate }) => {
        updateCachedCampaign(campaignId, (draftCampaign) => {
          draftCampaign.start_date = startDate;
          draftCampaign.state = "SCHEDULED";
        });

        updateCachedCampaignInList(campaignId, {
          start_date: startDate,
          state: "SCHEDULED",
        });
      },
      onError: () => {
        toast.error("Failed to schedule campaign");
      },
    });

  const { mutateAsync: unscheduleCampaign, isPending: isUnschedulingCampaign } =
    useMutation({
      mutationFn: ({ campaignId }: { campaignId: string }) =>
        unscheduleCampaignRequest(workspaceId, campaignId),
      onSuccess: (_, { campaignId }) => {
        updateCachedCampaign(campaignId, (draftCampaign) => {
          draftCampaign.state = "CREATED";
        });

        updateCachedCampaignInList(campaignId, { state: "CREATED" });
      },
      onError: () => {
        toast.error("Failed to unschedule campaign");
      },
    });

  const { mutateAsync: duplicateCampaign, isPending: isDuplicatingCampaign } =
    useMutation({
      mutationFn: ({
        campaignId,
        accountIds,
        name,
        miner,
      }: {
        campaignId: string;
        accountIds: string[];
        name?: string;
        miner?: string;
      }) =>
        duplicateCampaignRequest(workspaceId, campaignId, {
          accountIds,
          name,
          miner,
        }),
      onError: () => {
        toast.error("Unable to duplicate campaign, please try again later");
      },
      onSuccess: (newCampaign) => {
        updateCachedCampaigns((campaigns) => [newCampaign, ...campaigns]);
      },
    });

  const {
    mutateAsync: removeAccountFromCampaign,
    isPending: isRemovingAccountFromCampaign,
  } = useMutation({
    mutationFn: ({
      campaignId,
      accountId,
    }: {
      campaignId: string;
      accountId: string;
    }) => removeAccountFromCampaignRequest(workspaceId, campaignId, accountId),
    onSuccess: (_, { campaignId, accountId }) => {
      updateCachedCampaign(campaignId, (draftCampaign) => {
        draftCampaign.owners = draftCampaign.owners.filter(
          (owner) => owner !== accountId,
        );
      });

      const detailedCampaign = queryClient.getQueryData(
        campaignsKeys.details(campaignId),
      ) as DetailedCampaign;

      updateCachedCampaignInList(campaignId, {
        owners: detailedCampaign.owners,
      });

      toast.success("Account removed");
    },
    onError: () => {
      toast.error("Failed to remove account from campaign");
    },
  });

  const {
    mutateAsync: addAccountToCampaign,
    isPending: isAddingAccountToCampaign,
  } = useMutation({
    mutationFn: ({
      campaignId,
      accountId,
    }: {
      campaignId: string;
      accountId: string;
    }) => addAccountToCampaignRequest(workspaceId, campaignId, accountId),
    onSuccess: (_, { campaignId, accountId }) => {
      updateCachedCampaign(campaignId, (draftCampaign) => {
        draftCampaign.owners = [accountId, ...draftCampaign.owners];
      });

      const detailedCampaign = queryClient.getQueryData(
        campaignsKeys.details(campaignId),
      ) as DetailedCampaign;

      updateCachedCampaignInList(campaignId, {
        owners: detailedCampaign.owners,
      });

      toast.success("Account added");
    },
    onError: () => {
      toast.error("Failed to add an account to the campaign");
    },
  });

  return {
    createCampaign,
    isCreatingCampaign,
    updateCampaign,
    isUpdatingCampaign,
    deleteCampaign,
    isDeletingCampaign,
    scheduleCampaign,
    isSchedulingCampaign,
    unscheduleCampaign,
    isUnschedulingCampaign,
    duplicateCampaign,
    isDuplicatingCampaign,
    removeAccountFromCampaign,
    isRemovingAccountFromCampaign,
    addAccountToCampaign,
    isAddingAccountToCampaign,
  };
}
